import { inject } from "mobx-react";
import { useCallback, useEffect, useState } from "react";
import type { Store } from "@inferno/renderer-shared-core";

import { Remote } from "../../../../../core/components/remote/Remote.component";
import {
  PWSMemberServiceBase,
  isPWSSessionBackend,
  PopupState,
  PWSSessionState,
  COAST_MESSAGES,
} from "@inferno/renderer-shared-core";
import { axiosLoaderWrapper } from "../../../lib/coastCommon";
import { getUserSettingsLoaderCacheKey } from "../../../lib/coastUser";
import { CoastUserSettings, UserSettingBillingData, UserSettingUserData } from "./CoastUserSettings.component";

interface CoastUserSettingsLoaderProps {
  viewData: PopupState["data"];
  store?: Store;
}

export interface UserSettingsLoaderData {
  userData: UserSettingUserData;
  billingData: UserSettingBillingData;
}

const userSettingsLoader = async (
  memberService: PWSMemberServiceBase,
  accessToken?: string | null,
): Promise<UserSettingsLoaderData> =>
  accessToken
    ? Promise.all([
        axiosLoaderWrapper(() => memberService.getUser(accessToken), COAST_MESSAGES.ACCOUNT_FETCHING_ERROR),
        axiosLoaderWrapper(() => memberService.getBillingInfo(accessToken), COAST_MESSAGES.ACCOUNT_FETCHING_ERROR),
      ]).then(([userData, billingData]) => ({ userData, billingData }))
    : { userData: { data: null }, billingData: { data: null } };

export const CoastUserSettingsLoader = inject("store")(({ store }: CoastUserSettingsLoaderProps) => {
  const session = store?.session.currentSession;

  if (!store || !isPWSSessionBackend(session)) {
    return null;
  }

  const [accessToken, setAccessToken] = useState(session.state.accessToken);

  const handleSessionUpdate = useCallback((state: PWSSessionState) => setAccessToken(state.accessToken), []);

  useEffect(() => {
    session.onStatusChanged.subscribe(handleSessionUpdate);
    return () => session.onStatusChanged.unsubscribe(handleSessionUpdate);
  }, [handleSessionUpdate, session.onStatusChanged]);

  return (
    <Remote
      loader={() => userSettingsLoader(session.memberService, accessToken)}
      cacheKey={getUserSettingsLoaderCacheKey(accessToken)}
      showLoading={true}
    >
      {({ data }) => (data ? <CoastUserSettings userData={data.userData} billingData={data.billingData} /> : null)}
    </Remote>
  );
});
